import React from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { Download as DownloadIcon } from '@mui/icons-material';
import { pdf } from '@react-pdf/renderer';
import moment from 'moment';
import HeaderTitle from '../../../../components/PageHeader/HeaderTitle';
import { DailyReportModel } from '../../../../models/audit/DailyReportModel';
import { AuditReportPdfDocument, AuditPdfMode } from './auditReportPdf';

interface AuditReportHeaderProps {
  dailyReportResponse: DailyReportModel | null | undefined;
  isAllShift: boolean;
  storeName?: string;
  storeAddress?: string;
  isBothConnected: boolean;
  isBothNotConnected: boolean;
  isLottoConnectedOnly: boolean;
  isPOSConnectedOnly: boolean;
}

const resolvePdfMode = ({
  isBothConnected,
  isBothNotConnected,
  isLottoConnectedOnly,
  isPOSConnectedOnly,
}: Pick<AuditReportHeaderProps, 'isBothConnected' | 'isBothNotConnected' | 'isLottoConnectedOnly' | 'isPOSConnectedOnly'>): AuditPdfMode => {
  if (isBothConnected) return 'both_connected';
  if (isLottoConnectedOnly) return 'lotto_only';
  if (isPOSConnectedOnly) return 'pos_only';
  if (isBothNotConnected) return 'both_not_connected';
  return 'both_not_connected';
};

const AuditReportHeader: React.FC<AuditReportHeaderProps> = ({
  dailyReportResponse,
  isAllShift,
  storeName,
  storeAddress,
  isBothConnected,
  isBothNotConnected,
  isLottoConnectedOnly,
  isPOSConnectedOnly,
}) => {
  const [isDownloading, setIsDownloading] = React.useState(false);
  const reportDateRaw = (dailyReportResponse as any)?._serverData?.report_date;
  const hasRequiredHeaderData = Boolean(dailyReportResponse && storeName && storeAddress && reportDateRaw);

  const handleDownloadReport = async () => {
    if (isDownloading || !hasRequiredHeaderData) return;

    const reportDate = moment(reportDateRaw).isValid()
      ? moment(reportDateRaw).format('MM/DD/YYYY')
      : String(reportDateRaw);

    const mode = resolvePdfMode({
      isBothConnected,
      isBothNotConnected,
      isLottoConnectedOnly,
      isPOSConnectedOnly,
    });

    try {
      setIsDownloading(true);
      const blob = await pdf(
        <AuditReportPdfDocument
          mode={mode}
          storeName={String(storeName)}
          address={String(storeAddress)}
          reportDate={reportDate}
          dailyReportResponse={dailyReportResponse as DailyReportModel}
        />
      ).toBlob();

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Audit_Report_${storeName}_${moment(reportDateRaw).format('YYYY-MM-DD')}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to generate audit report PDF:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Box className="page-header mb-8" sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <HeaderTitle title="Audit Detail" />
      {isAllShift && (
        <Button
          variant="contained"
          onClick={handleDownloadReport}
          disabled={isDownloading || !hasRequiredHeaderData}
          className="custom-button"
          sx={{
            backgroundColor: '#2C6CA0',
            borderRadius: '8px',
            px: 1.5,
            py: 0.5,
            minHeight: 38.5,
            minWidth: 180,
            textTransform: 'none',
            fontSize: 15,
            fontWeight: 500,
            opacity: isDownloading || !hasRequiredHeaderData ? 0.7 : 1,
            '&:hover': { backgroundColor: '#244e7a' },
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 18, height: 18, display: 'grid', placeItems: 'center' }}>
              {isDownloading ? (
                <CircularProgress size={18} sx={{ color: '#fff' }} />
              ) : (
                <DownloadIcon fontSize="small" sx={{ color: '#fff', width: 18, height: 18 }} />
              )}
            </Box>
            <span>Download Report</span>
          </Box>
        </Button>
      )}
    </Box>
  );
};

export default AuditReportHeader;
